import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { BillOfMaterialsType } from 'src/types/billOfMaterials';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import ProductsTable from './ProductsTable';
import TopBlock from './TopBlock';
import { EDIT_KEYS, EditKeysType, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import NewProductBlock from './NewProductBlock';
import {
    clearBillOfMaterialsDetail,
    createBillOfMaterialsDetailFetch,
    getBillOfMaterialsDetailFetch,
    updateBillOfMaterialsDetailFetch
} from 'src/resources/billOfMaterialsDetail/reducer';
import { sizes } from 'src/themes/default';
import isDeepEqual from 'src/utils/isDeepEqual';
import { setStorageData } from 'src/resources/storage/reducer';
import { samePaths } from 'src/utils/checkPaths';
import { FormErrorsType, OnChangeType } from '../../types/global';

const initData = {};
const BillOfMaterialsDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idBillOfMaterials, idProduct } = useParams();

    const newBillsOfMaterials = !idBillOfMaterials;

    const formRef = useRef<HTMLFormElement>(null);
    const BillsOfMaterialsFormRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.billOfMaterialsDetail.data);
    const loading = useSelector((state: RootStoreType) => state.billOfMaterialsDetail.loading);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const [editData, setEditData] = useState<BillOfMaterialsType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const disableEdit = newBillsOfMaterials
        ? !useCheckPermission('BILL_OF_MATERIALS.CREATE')
        : !useCheckPermission('BILL_OF_MATERIALS.UPDATE');

    const backUrl = idProduct ? `/products/${idProduct}/bill_of_materials/` : '/products/bill_of_materials/';

    useEffect(() => {
        if (idBillOfMaterials) {
            dispatch(
                setLocationParams({ name: translate('billOfMaterialsDetail.title', { idBillOfMaterials }), backUrl })
            );
        } else {
            dispatch(setLocationParams({ name: translate('billOfMaterialsDetail.titleNew'), backUrl }));
        }
    }, [idBillOfMaterials, backUrl, locationHistory]);

    useEffect(() => {
        if (idBillOfMaterials) {
            dispatch(getBillOfMaterialsDetailFetch(idBillOfMaterials));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearBillOfMaterialsDetail());
        };
    }, [idBillOfMaterials]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idBillOfMaterials && data?.ID) {
            navigate('/products/bill_of_materials/' + data.ID);
            setThisStorageData(null);
        }
    }, [data]);

    // save edit data -start
    const editDataRef = useRef<BillOfMaterialsType>({});
    useEffect(() => {
        if (
            newBillsOfMaterials &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/products/bill_of_materials') ||
                samePaths(locationHistory[1]?.pathname, '/products/bill_of_materials/new'))
        ) {
            setConfirmParams({
                title: translate('billOfMaterialsDetail.confirmGetNewDataStorage'),
                content: translate('billOfMaterialsDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                }
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: BillOfMaterialsType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeProducts: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        if (value.length) {
            delete newErrors.PRODUCTS;
        } else {
            newErrors.PRODUCTS = true;
        }
        if (errorKey) {
            newErrors[errorKey] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);
        if (!editData?.PRODUCTS?.length) {
            newFormErrors.PRODUCTS = true;
        }

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(newBillsOfMaterials ? 'billOfMaterialsDetail.create' : 'billOfMaterialsDetail.save'),
                content: translate(
                    newBillsOfMaterials ? 'billOfMaterialsDetail.createConfirm' : 'billOfMaterialsDetail.saveConfirm'
                ),
                action: () => {
                    const formatData = {
                        ...editData,
                        PRODUCTS: editData?.PRODUCTS?.map((product) => {
                            const formatBillOfMaterials = { ...product };
                            if ((formatBillOfMaterials.ID || -1) < 0) {
                                delete formatBillOfMaterials.ID;
                            }
                            return formatBillOfMaterials;
                        })
                    };
                    delete formatData.FINAL_PRODUCT;
                    if (newBillsOfMaterials) {
                        dispatch(createBillOfMaterialsDetailFetch(formatData));
                    } else {
                        dispatch(updateBillOfMaterialsDetailFetch({ idBillOfMaterials, data: formatData }));
                    }
                }
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock newBillsOfMaterials={newBillsOfMaterials} disableEdit={disableEdit} handleSave={handleSave} />
            {loading && <Loading />}

            <FormBlock sx={{ marginBottom: sizes.blocks_top_MG }}>
                <form ref={formRef}>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newBillsOfMaterials={newBillsOfMaterials}
                        disableEdit={disableEdit}
                    />
                </form>
            </FormBlock>
            <FormBlock>
                <NewProductBlock
                    data={editData.PRODUCTS || []}
                    onChange={(value: any) => onChange('PRODUCTS', value, false)}
                    disableEdit={disableEdit}
                    editErrors={editErrors}
                />
            </FormBlock>
            <form ref={BillsOfMaterialsFormRef}>
                <ProductsTable
                    newBillsOfMaterials={newBillsOfMaterials}
                    data={editData.PRODUCTS}
                    editErrors={editErrors}
                    onChange={(value: any, error: boolean, errorKey?: string) => {
                        onChangeProducts('PRODUCTS', value, error, errorKey);
                    }}
                    disableEdit={disableEdit}
                />
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default BillOfMaterialsDetailContainer;
